import React, { useState } from "react";
import { GlassCard, SubNav, FileDropzone } from "../gc/ui";
import { ENGINES_2D, GENRES_2D, downloadText, readAsDataUrl, downloadDataUrl, worldTilesetPng, SKIN_PALETTES } from "../../lib/gamecraft";
import SkinPicker from "../gc/SkinPicker";
import CameraModePicker from "../gc/CameraModePicker";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Download, FloppyDisk, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const TABS = [
  { id: "general", label: "General" },
  { id: "config", label: "Configuración" },
  { id: "style", label: "Estilo" },
  { id: "refs", label: "Referencias" },
  { id: "store", label: "Almacén" },
  { id: "backups", label: "Backups" },
];

export default function ProjectPanel({ project, onChange }) {
  const [tab, setTab] = useState("general");
  const gdd = project.gdd || {};
  const cfg = project.config || {};
  const refs = project.references || [];

  const setGdd = (patch) => onChange({ gdd: { ...gdd, ...patch } });
  const setCfg = (patch) => onChange({ config: { ...cfg, ...patch } });

  const addRef = async (files) => {
    const next = [...refs];
    for (const f of files) {
      const data_url = await readAsDataUrl(f);
      next.push({ id: crypto.randomUUID(), name: f.name, data_url, type: f.type });
    }
    onChange({ references: next, tags: project.tags });
  };

  const backup = () => {
    const clone = { ...project };
    downloadText(JSON.stringify(clone, null, 2), `${project.slug || "proyecto"}-backup.json`, "application/json");
    toast.success("Backup descargado");
  };

  const restore = async (files) => {
    try {
      const data = JSON.parse(await files[0].text());
      const { project_id, owner_id, slug, ...rest } = data;
      onChange(rest);
      toast.success("Backup restaurado (revisa y guarda)");
    } catch {
      toast.error("Backup inválido");
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4">
      <div>
        <h2 className="font-display text-2xl font-semibold">Proyecto</h2>
        <p className="text-sm" style={{ color: "var(--gc-muted)" }}>Define el juego, su estilo 2D y las referencias visuales.</p>
      </div>
      <SubNav items={TABS} value={tab} onChange={setTab} />

      {tab === "general" && (
        <GlassCard className="p-5 space-y-4">
          <Field label="Nombre del proyecto">
            <Input className="gc-input mt-1" value={project.name} onChange={(e) => onChange({ name: e.target.value })} />
          </Field>
          <Field label="Idea general">
            <Textarea className="gc-input mt-1" rows={4} value={gdd.idea || ""} onChange={(e) => setGdd({ idea: e.target.value })} />
          </Field>
          <Field label="Descripción">
            <Textarea className="gc-input mt-1" rows={3} value={project.description || ""} onChange={(e) => onChange({ description: e.target.value })} />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Género">
              <select className="gc-input h-10 px-3 w-full mt-1" value={project.genre} onChange={(e) => onChange({ genre: e.target.value })}>
                {GENRES_2D.map((g) => <option key={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Plataforma">
              <Input className="gc-input mt-1" value={cfg.platform || "PC / Web"} onChange={(e) => setCfg({ platform: e.target.value })} />
            </Field>
          </div>
          <Field label="Etiquetas del proyecto">
            <Input
              className="gc-input mt-1"
              placeholder="rpg, fantasy, pixelart (separadas por coma)"
              value={(project.tags || []).join(", ")}
              onChange={(e) => onChange({ tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
            />
          </Field>
        </GlassCard>
      )}

      {tab === "config" && (
        <GlassCard className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Resolución">
            <select className="gc-input h-10 px-3 w-full mt-1" value={cfg.resolution || "1920x1080"} onChange={(e) => setCfg({ resolution: e.target.value })}>
              {["1280x720", "1600x900", "1920x1080", "2560x1440", "640x360 (pixel perfecto)"].map((r) => <option key={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="FPS objetivo">
            <select className="gc-input h-10 px-3 w-full mt-1" value={String(cfg.fps || 60)} onChange={(e) => setCfg({ fps: +e.target.value })}>
              {[30, 48, 60, 120].map((n) => <option key={n} value={n}>{n} FPS</option>)}
            </select>
          </Field>
          <Field label="Motor">
            <select className="gc-input h-10 px-3 w-full mt-1" value={project.engine} onChange={(e) => onChange({ engine: e.target.value })}>
              {ENGINES_2D.map((e) => <option key={e}>{e}</option>)}
            </select>
          </Field>
          <Field label="Lenguaje de código">
            <select className="gc-input h-10 px-3 w-full mt-1" value={cfg.language || "javascript"} onChange={(e) => setCfg({ language: e.target.value })}>
              <option value="javascript">JavaScript / Phaser</option>
              <option value="python">Python / Pygame</option>
              <option value="gdscript">GDScript / Godot</option>
              <option value="csharp">C# / Unity 2D</option>
              <option value="lua">Lua</option>
            </select>
          </Field>
          <Field label="Cámara 2D">
            <div className="mt-1 md:col-span-2">
              <CameraModePicker value={cfg.camera || "side"} onChange={(camera) => setCfg({ camera })} />
            </div>
          </Field>
        </GlassCard>
      )}

      {tab === "style" && (
        <GlassCard className="p-5 space-y-4">
          <Field label="Estilo visual">
            <select className="gc-input h-10 px-3 w-full mt-1" value={cfg.visual_style || "Pixel Art"} onChange={(e) => setCfg({ visual_style: e.target.value })}>
              {["Pixel Art", "HD 2D", "Vectorial", "Hand-painted", "Low-res retro", "Paper cutout"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <div>
            <div className="text-xs font-semibold mb-2">Tema del proyecto</div>
            <p className="text-xs mb-3" style={{ color: "var(--gc-muted)" }}>Mundos con degradados y textura. Elige qué capas pinta el tema.</p>
            <SkinPicker
              value={cfg.skin || "aurora"}
              applyTargets={cfg.theme_apply}
              onApplyChange={(theme_apply) => setCfg({ theme_apply })}
              bgPreset={cfg.bg_preset}
              bgImage={cfg.bg_image}
              bgUploads={cfg.bg_uploads || []}
              bgSource={cfg.bg_source}
              onBgChange={(patch) => setCfg(patch)}
              onSelect={(patch) => {
                const pal = SKIN_PALETTES[patch.skin] || cfg.palette;
                const name = `mundo_${patch.skin}`;
                const sprites = project.sprites || [];
                const has = sprites.some((s) => s.name === name);
                const nextSprites = has ? sprites : [...sprites, {
                  id: crypto.randomUUID(),
                  name,
                  data_url: worldTilesetPng(patch.skin),
                  studio: "world",
                  category: "tileset",
                  width: 16,
                  height: 16,
                }];
                onChange({ config: { ...cfg, ...patch, palette: pal, bg_image: "", bg_source: "preset", bg_preset: patch.skin }, sprites: nextSprites });
                toast.success(`Tema ${patch.skin} · paleta y tileset listos`);
              }}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Acento">
              <input type="color" className="h-10 w-full rounded-xl cursor-pointer mt-1" value={cfg.accent || "#4c7dff"} onChange={(e) => setCfg({ accent: e.target.value })} />
            </Field>
            <Field label="Fondo">
              <input type="color" className="h-10 w-full rounded-xl cursor-pointer mt-1" value={cfg.bg || "#eef1f6"} onChange={(e) => setCfg({ bg: e.target.value })} />
            </Field>
            <Field label="Editor (canvas oscuro)">
              <input type="color" className="h-10 w-full rounded-xl cursor-pointer mt-1" value={cfg.tool_bg || "#0f172a"} onChange={(e) => setCfg({ tool_bg: e.target.value })} />
            </Field>
          </div>
          <Field label="Paleta objetivo (hex, uno por línea)">
            <Textarea className="gc-input mt-1 font-mono text-sm" rows={5} value={cfg.palette_notes || ""} onChange={(e) => setCfg({ palette_notes: e.target.value })} placeholder={"#4c7dff\n#22c55e\n#1e293b"} />
          </Field>
          <Field label="Notas de arte">
            <Textarea className="gc-input mt-1" rows={4} value={cfg.art_notes || ""} onChange={(e) => setCfg({ art_notes: e.target.value })} />
          </Field>
        </GlassCard>
      )}

      {tab === "refs" && (
        <GlassCard className="p-5 space-y-4">
          <FileDropzone accept="image/*" multiple onFiles={addRef} label="Subir referencias" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {refs.map((r) => (
              <div key={r.id} className="rounded-2xl overflow-hidden border" style={{ borderColor: "var(--gc-border)" }}>
                <img src={r.data_url} alt={r.name} className="w-full h-28 object-cover" />
                <div className="p-2 flex items-center justify-between text-[11px]">
                  <span className="truncate">{r.name}</span>
                  <div className="flex gap-1">
                    <button onClick={() => downloadDataUrl(r.data_url, r.name)}><Download size={12} /></button>
                    <button className="text-red-500" onClick={() => onChange({ references: refs.filter((x) => x.id !== r.id) })}><Trash size={12} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {tab === "store" && (
        <GlassCard className="p-5 space-y-2 text-sm" style={{ color: "var(--gc-muted)" }}>
          <p>Sprites: {(project.sprites || []).length} · Audio: {(project.samples || []).length} · Código: {(project.files || []).length} · Referencias: {refs.length}</p>
          <p>Todo se guarda en el proyecto (nube). Usa Backups para una copia local.</p>
        </GlassCard>
      )}

      {tab === "backups" && (
        <GlassCard className="p-5 flex flex-wrap gap-3">
          <button className="gc-btn gc-btn-primary h-10 px-4" onClick={backup}><FloppyDisk size={14} /> Descargar backup JSON</button>
          <FileDropzone accept="application/json" onFiles={restore} label="Restaurar backup" />
        </GlassCard>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-semibold" style={{ color: "var(--gc-muted)" }}>{label}</div>
      {children}
    </label>
  );
}
